<template>
    <div class="page-content">
        <loader v-if="isLoading" />
        <div
            v-else
            class="d-flex flex-md-row flex-column justify-content-between mb-4"
        >
            <div class="role-group flex-wrap mb-2 mb-md-0">
                <permissions-role-button
                    v-for="role in roles"
                    :key="role.name"
                    :role="role"
                    :selectedRole="selectedRole"
                    :isHasAccessToPermission="isHasAccessToPermission"
                    :changeSelectedRole="changeSelectedRole"
                />
            </div>
            <div class="flex-grow-1">
                <input-search-icon
                    v-model="permissionKeyword"
                    @search="searchingPermission"
                    placeholder="Search permission"
                />
            </div>
        </div>

        <PermissionsByRole
            v-if="isHasAccessToPermission || isLoading"
            :isParentLoading="isLoading || isLoadingOnRoleChanged"
            :role="selectedRole"
            :modules="isFilteredPermissions ? filteredModules : modules"
            :permissions="
                isFilteredPermissions ? filteredPermissions : permissions
            "
            :lastUpdatedDuration="lastUpdatedDuration"
            :updatePermission="updatePermission"
        />

        <b-card v-else class="side-tab">
            <p class="m-0">
                Your role doesn’t have access to permissions settings.
            </p>
        </b-card>
    </div>
</template>
<script>
import PermissionsByRole from "@/components/setting-permissions/permissions-by-role.vue";
import PermissionsRoleButton from "@/components/setting-permissions/permissions-role-button.vue";
import permissionService from "@/services/setting/permission";
import Loader from "@/components/elements/loader-custom";
import { find } from "lodash";
import PERMISSIONS from "@/constants/permissions";
import InputSearchIcon from "@/module/commons/input-search-icon.vue";

export default {
    components: {
        Loader,
        PermissionsByRole,
        PermissionsRoleButton,
        InputSearchIcon
    },
    computed: {
        isHasAccessToPermission() {
            return this._hasAccess("setting.permission.update") ? true : false;
        },
        isFilteredPermissions() {
            return this.permissionKeyword ? true : false;
        }
    },
    data() {
        return {
            isLoading: true,
            isLoadingOnRoleChanged: false,
            selectedRole: null,
            modules: [],
            filteredModules: [],
            roles: [],
            permissions: {},
            filteredPermissions: {},
            fetchedPermissions: [],
            lastUpdatedDuration: null,
            lastUpdatedInterval: null,
            lastUpdatedTimeoutSecond: null,
            lastUpdatedTimeoutMinute: null,
            permissionKeyword: ""
        };
    },
    mounted() {
        this.firstLoad();
    },
    methods: {
        async firstLoad() {
            this.isLoading = true;
            await this.getRoles();

            if (this.isHasAccessToPermission) {
                await this.getModules();
                await this.getPermissions();
            }

            this.isLoading = false;
        },
        async getRoles() {
            const fetchedRoles = await permissionService.getRoles();

            if (fetchedRoles && fetchedRoles.length) {
                this.roles = fetchedRoles;
                // NOTE: Select the second role, which is not the administrator role
                this.selectedRole = this.roles[1];
            }
        },
        async getModules() {
            const fetchedModules = await permissionService.getModules();

            if (fetchedModules && fetchedModules.length) {
                this.modules = fetchedModules;
            }
        },
        async getPermissions() {
            this.permissions = {};
            const fetchedPermissions =
                await permissionService.getPermissionsByRoleAndModules(
                    this.selectedRole.id,
                    this.modules.map(module => module.module)
                );

            this.fetchedPermissions = fetchedPermissions;

            if (this.isFilteredPermissions) {
                this.permissions = this.extractPermissions();
                this.searchingPermission(this.permissionKeyword);
            } else {
                this.permissions = this.extractPermissions();
            }
        },
        async changeSelectedRole(role) {
            if (this.isLoadingOnRoleChanged || !this.isHasAccessToPermission)
                return;

            this.isLoadingOnRoleChanged = true;

            this.resetIntervalAndTimeout();
            this.lastUpdatedDuration = null;

            this.selectedRole = role;
            await this.getModules();
            await this.getPermissions();

            this.isLoadingOnRoleChanged = false;
        },
        resetIntervalAndTimeout() {
            if (this.lastUpdatedInterval) {
                clearInterval(this.lastUpdatedInterval);
                clearTimeout(this.lastUpdatedTimeoutSecond);
                clearTimeout(this.lastUpdatedTimeoutMinute);

                this.lastUpdatedInterval = null;
                this.lastUpdatedTimeoutSecond = null;
                this.lastUpdatedTimeoutMinute = null;
            }
        },
        setIntervalAndTimeoutUpdatedDuration() {
            const UPDATED_DURATION_LAST = 3600000;

            this.resetIntervalAndTimeout();

            let updatedDate = this._now();

            // NOTE: Get duration in second
            const getDurationInSecond = () =>
                this._now().diff(updatedDate, ["seconds"]).toObject();

            // NOTE: Get duration in minute
            const getDurationInMinute = () =>
                this._now().diff(updatedDate, ["minutes"]).toObject();

            this.lastUpdatedDuration = getDurationInSecond();

            // NOTE: Set interval for first 1 minute so that we have increment 10 seconds
            this.lastUpdatedInterval = setInterval(() => {
                this.lastUpdatedDuration = getDurationInSecond();
            }, 10000);

            // NOTE: Remove prev interval and add new interval where it has 1 minute increment
            this.lastUpdatedTimeoutSecond = setTimeout(() => {
                clearInterval(this.lastUpdatedInterval);
                this.lastUpdatedDuration = getDurationInMinute();

                this.lastUpdatedInterval = setInterval(() => {
                    this.lastUpdatedDuration = getDurationInMinute();
                }, 60000);
            }, 60000);

            // NOTE: Remove last interval after UPDATED_DURATION_LAST milliseconds
            this.lastUpdatedTimeoutMinute = setTimeout(() => {
                clearInterval(this.lastUpdatedInterval);
                this.lastUpdatedDuration = null;
                this.lastUpdatedInterval = null;
            }, UPDATED_DURATION_LAST);
        },
        async updatePermission(permission) {
            // NOTE: There are 2 task permissions that depend on each other
            this.checkTaskPermissions(permission);

            this.lastUpdatedDuration = null;

            this.resetIntervalAndTimeout();
            let response = null;

            const { id, key, role_id, access } = permission;

            if (id) {
                response = await permissionService.updatePermission(id, access);
            } else if (key && role_id) {
                response = await permissionService.createPermission(
                    key,
                    role_id,
                    access
                );

                if (response && response.id) {
                    // NOTE: After creating a new permission, set the new permission ID in the permission object
                    // So, in the next toggle click, it will update the permission instead of creating new
                    // (In case user did not refresh the page)
                    permission.id = response.id;
                }
            }

            if (response) {
                this.setIntervalAndTimeoutUpdatedDuration();
            }
        },
        async checkTaskPermissions(permission) {
            // NOTE: if user update "Can edit all tasks?", "Can reassign all tasks?" also need to be updated
            if (permission.key === PERMISSIONS.KEY.SCHEDULE_TASK_UPDATE) {
                const reassignAllPermission = this.getTaskPermission(
                    PERMISSIONS.KEY.SCHEDULE_TASK_ASSIGN
                );

                if (reassignAllPermission) {
                    if (reassignAllPermission.access != permission.access) {
                        reassignAllPermission.access = permission.access;
                        await this.updatePermission(reassignAllPermission);
                    }
                }
                // NOTE: If user update "Can reassign all tasks?", find if "Can edit all tasks?" is checked
                // if checked, "Can reassign all tasks?" cannot be updated
            } else if (
                permission.key === PERMISSIONS.KEY.SCHEDULE_TASK_ASSIGN
            ) {
                const editAllPermission = this.getTaskPermission(
                    PERMISSIONS.KEY.SCHEDULE_TASK_UPDATE
                );

                if (editAllPermission.access) {
                    const reassignAllPermissionElement =
                        document.getElementById(
                            PERMISSIONS.KEY.SCHEDULE_TASK_ASSIGN
                        );

                    if (reassignAllPermissionElement) {
                        permission.access = 1;
                        reassignAllPermissionElement.checked = true;
                    }
                }
            }
        },
        getTaskPermission(key) {
            let taskPermissions = this.permissions
                ? this.permissions.schedule.submodules.tasks.permissions
                : null;

            let taskObject = taskPermissions
                ? find(taskPermissions, ["key", key])
                : null;

            return taskObject;
        },
        searchingPermission(keyword) {
            this.resetFilteredPermissions(keyword);

            if (this.permissionKeyword == "") {
                return;
            }

            this.filteredPermissions = this.extractPermissions(true);
            this.filterModules();
        },
        resetFilteredPermissions(keyword) {
            this.permissionKeyword = keyword;
            this.filteredModules = [];
            this.filteredPermissions = {};
        },
        filterModules() {
            // NOTE: Filter modules/submodules that has empty permissions
            for (const module in this.filteredPermissions) {
                const { permissions, submodules } =
                    this.filteredPermissions[module];

                if (!permissions.length && !Object.keys(submodules).length) {
                    delete this.filteredPermissions[module];
                } else {
                    this.filteredModules.push({ module });
                }
            }
        },
        extractPermissions(isFiltered = false) {
            if (this.fetchedPermissions && this.fetchedPermissions.length) {
                // NOTE: using dictionary instead of array
                // to avoid looping the API array response more than one time
                // Because the array responses can have items more than 300
                const permisssions = {};

                this.modules.forEach(module => {
                    permisssions[module.module] = {
                        permissions: [],
                        submodules: {}
                    };
                });

                // NOTE: The only looping needed to transform the raw array API response
                // To group permission by module and submodule
                this.fetchedPermissions.forEach(permission => {
                    if (
                        isFiltered &&
                        this.permissionKeyword &&
                        !permission.label
                            .toLowerCase()
                            .includes(
                                this.permissionKeyword.trim().toLowerCase()
                            )
                    ) {
                        return;
                    }

                    if (permission.submodule) {
                        if (
                            !permisssions[permission.module].submodules[
                                permission.submodule
                            ]
                        ) {
                            permisssions[permission.module].submodules[
                                permission.submodule
                            ] = { permissions: [] };
                        }

                        permisssions[permission.module].submodules[
                            permission.submodule
                        ].permissions.push(permission);
                    } else {
                        permisssions[permission.module].permissions.push(
                            permission
                        );
                    }
                });

                return permisssions;
            }
        }
    }
};
</script>
